import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Label } from 'flowbite-react';
import { Button, Badge } from '../../school_panel/components/ui';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { request, aiBatchGenerate } from '../../utils/remote';
import { useGrades } from '../../react-query/queries';
import DOMPurify from 'dompurify';

interface AIQuestionReviewDialogProps {
    show: boolean;
    onClose: () => void;
    questions: any[];
    sharedNotes?: any[];
    examId: string;
    onSaveComplete: () => void;
}


const AIQuestionReviewDialog: React.FC<AIQuestionReviewDialogProps> = ({ show, onClose, questions, sharedNotes, examId, onSaveComplete }) => {

    const { token } = useAuth();
    const [localQuestions, setLocalQuestions] = useState<any[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [isAssigningChapters, setIsAssigningChapters] = useState(false);

    const [showGradeSelect, setShowGradeSelect] = useState(false);
    const { data: gradesData } = useGrades(token);
    const [selectedGradeIds, setSelectedGradeIds] = useState<number[]>([]);

    useEffect(() => {
        if (show) {
            setLocalQuestions(questions.map(q => ({ ...q, chapter_id: null, chapter_name: null })));
        }
    }, [show, questions]);



    const handleAssignChapters = async () => {
        setIsAssigningChapters(true);
        try {
            const catResp = await request(`/api/school/exam-detail/${examId}/assign-chapters-ai/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ grade_ids: selectedGradeIds })
            });

            if (!catResp || !catResp.catalog) {
                throw new Error('Failed to fetch chapter catalog');
            }

            const catalog = catResp.catalog;

            const systemPrompt = (
                "You are an educational assistant. You are given a list of questions and a catalog of chapters. " +
                "For each question, identify the most relevant chapter from the catalog. " +
                "Return a JSON array of objects, each with 'question_idx' and 'chapter_id'. " +
                "If no chapter matches well, return null for chapter_id. Respond with the JSON array only. Return ONLY valid JSON. Do not include any introductory text, concluding text, or markdown formatting like \`\`\`json."
            );

            const inputData = localQuestions.map((q, i) => ({
                idx: i,
                text: q.question || q.ques,
                choices: [q.choice_a, q.choice_b, q.choice_c, q.choice_d]
            }));

            const userPrompt = `Catalog: ${JSON.stringify(catalog)}\n\nQuestions: ${JSON.stringify(inputData)}`;

            const result = await aiBatchGenerate({
                toBeValidated: token,
                messages: [{ role: 'user', content: `${systemPrompt}\n\n${userPrompt}` }],
                model: 'gemini-3.1-flash-lite',
            });

            let assignments: any[] = [];
            try {
                const responseText = result?.text ?? null;
                if (responseText) {
                    const jsonMatch = responseText.match(/(\[\s*\{.*\}\s*\])/s);
                    const cleanJson = jsonMatch ? jsonMatch[1] : responseText;
                    assignments = JSON.parse(cleanJson);
                }
            } catch (e) {
                
                throw new Error('AI returned invalid JSON');
            }

            const updated = [...localQuestions];
            assignments.forEach((as: any) => {
                if (as.question_idx !== undefined && updated[as.question_idx]) {
                    updated[as.question_idx].chapter_id = as.chapter_id;
                    const chap = catalog.find((c: any) => c.id === as.chapter_id);
                    if (chap) updated[as.question_idx].chapter_name = chap.name;
                }
            });

            setLocalQuestions(updated);
            setShowGradeSelect(false);

        } catch (error) {
            
            setActionError('Chapters could not be assigned by AI: ' + (error instanceof Error ? error.message : String(error)));
        } finally {
            setIsAssigningChapters(false);
        }
    };

    const handleSaveAll = async () => {
        setActionError(null);
        setIsSaving(true);
        try {
            const noteMapping: Record<number, number> = {};
            if (sharedNotes && sharedNotes.length > 0) {
                for (let i = 0; i < sharedNotes.length; i++) {
                    const note = sharedNotes[i];
                    try {
                        const noteResp = await request('/api/shared-notes/', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify(note)
                        });
                        if (noteResp && noteResp.id) {
                            noteMapping[i] = noteResp.id;
                        }
                    } catch (e) {
                        
                    }
                }
            }

            const questionsWithNotes = localQuestions.map(q => {
                const copy = { ...q };
                if (q.note_idx !== undefined && noteMapping[q.note_idx] !== undefined) {
                    copy.shared_note = noteMapping[q.note_idx];
                }
                return copy;
            });

            await request(`/api/school/exam-detail/${examId}/add-other-questions/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ questions: questionsWithNotes })
            });

            onSaveComplete();
        } catch (error) {
            
            setActionError('The questions could not be saved. Try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const toggleGrade = (id: number) => {
        setSelectedGradeIds(prev =>
            prev.includes(id) ? prev.filter(gid => gid !== id) : [...prev, id]
        );
    };

    const renderLatex = (html: string) => {
        if (!html) return '';
        return DOMPurify.sanitize(html);
    };

    return (
        <Modal show={show} onClose={onClose} size="6xl" className="z-[11000]" style={{ zIndex: 11000 }}> 
            <Modal.Header>Review extracted questions</Modal.Header>
            <Modal.Body>
                <div className="flex justify-between items-center mb-6">
                    <p className="text-bodydark2">Review the questions extracted by AI. You can also have AI assign chapters to them.</p>
                    <Button onClick={() => setShowGradeSelect(true)}>Generate chapters</Button>
                </div>

                {actionError && <p role="alert" className="mb-3 text-sm text-danger">{actionError}</p>}
                <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                    {localQuestions.map((q, idx) => (
                        <div key={idx} className="p-4 border rounded-lg bg-gray-50 dark:bg-gray-800 relative">
                            <div className="absolute top-2 right-2">
                                {q.chapter_id ? (
                                    <Badge tone="success">{q.chapter_name || `Chapter ${q.chapter_id}`}</Badge>
                                ) : (
                                    <Badge tone="warning">No chapter</Badge>
                                )}
                            </div>
                            <div className="font-semibold mb-2 flex justify-between items-center">
                                <span>Question {idx + 1}</span>
                                {q.note_idx !== undefined && sharedNotes && sharedNotes[q.note_idx] && (
                                    <Badge tone="info">Shared note: {sharedNotes[q.note_idx].title}</Badge>
                                )}
                            </div>
                            <div className="prose prose-sm dark:prose-invert mb-4" dangerouslySetInnerHTML={{ __html: renderLatex(q.question || q.ques) }} />
                            
                            <div className="grid grid-cols-1 gap-4 text-sm mb-4 sm:grid-cols-2">
                                <div className="p-2 bg-white dark:bg-gray-700 rounded border">
                                    <span className="font-semibold mr-2">A:</span>
                                    <span dangerouslySetInnerHTML={{ __html: renderLatex(q.choice_a) }} />
                                </div>
                                <div className="p-2 bg-white dark:bg-gray-700 rounded border">
                                    <span className="font-semibold mr-2">B:</span>
                                    <span dangerouslySetInnerHTML={{ __html: renderLatex(q.choice_b) }} />
                                </div>
                                <div className="p-2 bg-white dark:bg-gray-700 rounded border">
                                    <span className="font-semibold mr-2">C:</span>
                                    <span dangerouslySetInnerHTML={{ __html: renderLatex(q.choice_c) }} />
                                </div>
                                <div className="p-2 bg-white dark:bg-gray-700 rounded border">
                                    <span className="font-semibold mr-2">D:</span>
                                    <span dangerouslySetInnerHTML={{ __html: renderLatex(q.choice_d) }} />
                                </div>
                            </div>

                            <div className="flex gap-4 items-center">
                                <Badge tone="info">Answer: {q.answer || q.ans}</Badge>
                            </div>

                            {q.explanation && (
                                <div className="mt-4 p-3 bg-secondary/5 rounded border border-secondary/20">
                                    <div className="text-xs font-semibold text-secondary mb-1">AI explanation</div>
                                    <div className="text-sm italic" dangerouslySetInnerHTML={{ __html: renderLatex(q.explanation) }} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <Modal show={showGradeSelect} onClose={() => setShowGradeSelect(false)} size="md" className="z-[12000]" style={{ zIndex: 12000 }}>
                    <Modal.Header>Select grades for chapter assignment</Modal.Header>
                    <Modal.Body>
                        <div className="space-y-4">
                            <p className="text-sm text-gray-500">AI will look for chapters from these grades to assign to your questions.</p>
                            <div className="max-h-60 overflow-y-auto border rounded p-3">
                                {gradesData?.grades?.map((g: any) => (
                                    <div key={g.id} className="flex items-center gap-2 py-2">
                                        <Checkbox id={`grade-assign-${g.id}`} checked={selectedGradeIds.includes(g.id)} onChange={() => toggleGrade(g.id)} />
                                        <Label htmlFor={`grade-assign-${g.id}`}>{g.grade_name}</Label>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-end gap-2 mt-4">
                                <Button variant="ghost" onClick={() => setShowGradeSelect(false)}>Cancel</Button>
                                <Button onClick={handleAssignChapters} loading={isAssigningChapters}>Assign chapters</Button>
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            </Modal.Body>
            <Modal.Footer>
                <div className="flex justify-end w-full gap-4">
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSaveAll} loading={isSaving} disabled={localQuestions.length === 0}>
                        Save questions
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default AIQuestionReviewDialog;
